import React from "react";
import ClassNames from "classnames";
import "./MenuItemTabs.scss";
import { Link } from "react-scroll";
import { Tooltip, SkeletonLoader } from "@SeemplicityDev/shared";

export interface MenuTabItem {
  id: string;
  title: string;
  icon: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  isTitleLoading?: boolean;
  withTooltip?: { tooltipContent?: string };
}

export interface MenuItemTabsProps {
  items?: MenuTabItem[];
  width?: string | null;
  fullWidth?: boolean;
  "data-testid"?: string;
  isVertical?: boolean;
  containerId?: string;
  isLoading?: boolean;
  onSetActiveTab?: React.ComponentProps<typeof Link>["onSetActive"];
}

const MenuItemTabs: React.FC<MenuItemTabsProps> = ({
  items,
  width = null,
  fullWidth = false,
  "data-testid": dataTestId,
  isVertical = false,
  containerId,
  isLoading,
  onSetActiveTab,
}) => {
  return (
    <ul
      className={ClassNames("menu-item-tabs-list", {
        "is-vertical": isVertical,
        "is-horizontal": !isVertical,
        "is-fullwidth": fullWidth,
      })}
      data-testid={dataTestId}
    >
      {isLoading ? (
        <>
          {Array.from({ length: items?.length || 4 }, (_, i) => (
            <div
              key={i}
              className={"menu-item-tab is-loading"}
              data-testid={"menu-item-loading-container"}
            >
              <SkeletonLoader height={"40px"} width={"100%"} />
            </div>
          ))}
        </>
      ) : (
        !!items?.length &&
        items?.map((item) => (
          <Link
            key={`link-menu-${item.id}`}
            activeClass="menu-item-tab-link-active"
            className={`menu-item-tab-link ${item.isDisabled ? "is-disabled" : ""}`}
            to={
              // @types/react-scroll types `to` as a required string; preserve
              // original runtime value (`null` when disabled) without changing behavior.
              (!item.isDisabled ? item.id : null) as string
            }
            spy={true}
            smooth={true}
            duration={250}
            offset={-40}
            containerId={containerId}
            data-testid={`${dataTestId}-${item.id}-link`}
            onSetActive={onSetActiveTab}
          >
            <Tooltip type="info" content={item.withTooltip?.tooltipContent}>
              <li
                key={item.id}
                className={ClassNames("menu-item-tab", {
                  "is-disabled": item.isDisabled,
                  "is-loading": item.isLoading,
                })}
                // `width` is `string | null` at runtime; CSS `width` typing
                // rejects `null`, so coalesce to `undefined` (same rendered result).
                style={{ width: width ?? undefined }}
                data-testid={`${dataTestId}-${item.id}-content`}
              >
                <i className={`menu-item-tab-icon ${item.icon}`} />
                <div className="menu-item-tab-title-wrapper">
                  <span className="menu-item-tab-title">{item.title}</span>
                  {item.isTitleLoading && (
                    <i className="seem-icon-spinner spinner" />
                  )}
                </div>
              </li>
            </Tooltip>
          </Link>
        ))
      )}
    </ul>
  );
};

export default MenuItemTabs;
